'use client'

import { useState } from 'react'
import { 
  Dialog, 
  DialogContent, 
  DialogTitle, 
  DialogDescription
} from '@/components/ui/dialog'
import Button from '@/components/ui/Button'
import Image from 'next/image'
import { Switch } from '@/components/ui/switch'
import { useRouter } from 'next/navigation'
import { getUserData } from '@/app/(main)/agency/(auth)/helper'
import { selfServiceCards } from '@/utils/constants'

export function TrialModal({
  open,
  onOpenChange,
  redirectURL
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  redirectURL: string
}) {
  const [isYearly, setIsYearly] = useState(false)
  const router = useRouter()

  // Get Business Trial and Pro plans from selfServiceCards
  const businessPlan = selfServiceCards.find(card => card.title === 'Business Trial')
  const proPlan = selfServiceCards.find(card => card.title === 'Pro')

  if (!businessPlan || !proPlan) {
    console.error('Business Trial or Pro plan not found in selfServiceCards')
    return null
  }

  const navigateToBusinessPlan = () => {
    const user = getUserData()
    if (!user) {
      throw new Error('Failed to get user details')
    }
    const planName = isYearly ? 'basic' : 'promo'
    const agencyId = user?.agencyId
    const isComingFromGrapes = false
    const checkoutUrl = `/agency/${agencyId}/payment-checkout?plan=${encodeURIComponent(planName)}&yearly=${isYearly}&agencyId=${agencyId}&isComingFromGrapes=${isComingFromGrapes}&fromSettings=true&redirectURL=${redirectURL}`
    router.push(checkoutUrl)
  }
  
  const navigateToProPlan = () => {
    const user = getUserData()
    if (!user) {
      throw new Error('Failed to get user details')
    }
    const planName = 'pro'
    const agencyId = user?.agencyId
    const isComingFromGrapes = false
    const checkoutUrl = `/agency/${agencyId}/payment-checkout?plan=${encodeURIComponent(planName)}&yearly=${isYearly}&agencyId=${agencyId}&isComingFromGrapes=${isComingFromGrapes}&fromSettings=true&redirectURL=${redirectURL}`
    router.push(checkoutUrl)
  }
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-[800px] p-6 pt-10 bg-white rounded-3xl border-0 overflow-hidden">
        {/* Add proper DialogTitle and DialogDescription for accessibility */}
        <DialogTitle className="sr-only">Update your Plan</DialogTitle>
        <DialogDescription className="sr-only">
          Explore the right plan tailored to match your specific requirements and ambitions.
        </DialogDescription>
        
        {/* Simple X in the top right as seen in the image */}
        <button 
          onClick={() => onOpenChange(false)} 
          className="absolute right-4 top-4 z-10 text-gray-500"
          aria-label="Close dialog"
        >
          
        </button>
        
        <div className="px-2">
          <h1 className="text-[28px] font-bold text-center mb-2">Update your Plan</h1>
          <p className="text-gray-600 text-center mb-6">
            Explore the right plan tailored to match your specific requirements and ambitions.
          </p>
          
          <div className="flex items-center justify-center gap-3 mb-8">
            <span className="text-gray-700">Monthly</span>
            <Switch
              checked={isYearly}
              onCheckedChange={setIsYearly}
              variant="orange"
            />
            <span className="text-gray-700">Yearly</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Business Plan Card */}
            <div className="border border-gray-200 rounded-xl overflow-hidden relative">
              <div className="absolute top-0 left-0">
                <div className="bg-[#F46E3E] text-white text-xs font-medium py-1 px-3 rounded-br-lg">
                  Limited Time Offer
                </div>
              </div>
              <div className="p-6">
                <h2 className="text-xl font-bold mt-4">Business</h2>
                <p className="text-sm text-gray-600 mb-3">
                  {!isYearly ?businessPlan.description: "Everything you need to get started"}
                </p>
                
                <div className="mb-5">
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold">
                      {isYearly ? businessPlan.yearlyPrice : businessPlan.monthlyPrice}
                    </span>
                    <span className="text-sm text-gray-500 ml-1 line-through"> {!isYearly ? "$24.99":""}</span>
                    <span className="text-sm text-gray-500 ml-1">
                      {isYearly ? '/year' : '/month for 3 months'}
                    </span>
                  </div>
                </div>
                
                {/* Business plan button - orange background */}
                <Button 
                  className="w-full rounded-full bg-[#F46E3E] hover:bg-[#e05c2f] text-white font-medium py-3 mb-6"
                  onClick={navigateToBusinessPlan}
                >
                  {businessPlan.buttonText || 'Start Today'}
                </Button>
                
                <h3 className="font-bold mb-3">Features</h3>
                <ul className="space-y-3">
                  {businessPlan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <svg
                        width={20}
                        height={20}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#F46E3E"
                        strokeWidth={2}
                        className="mr-2 mt-0.5 flex-shrink-0"
                      >
                        <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span className="text-sm text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            {/* Pro Plan Card */}
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="p-6">
                <h2 className="text-xl font-bold mt-4">Pro</h2>
                <p className="text-sm text-gray-600 mb-3">
                  {proPlan.description}
                </p>
                
                <div className="mb-5">
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold">
                      {isYearly ? proPlan.yearlyPrice : proPlan.monthlyPrice}
                    </span>
                    <span className="text-sm text-gray-500 ml-1">
                      {isYearly ? '/year' : '/month'}
                    </span>
                  </div>
                </div>
                
                {/* Pro plan button - white background with border */}
                <button 
                  className="w-full rounded-full bg-white text-[#F46E3E] font-medium py-3 mb-6 border border-[#F46E3E] hover:bg-[#FFF0EB] transition-colors"
                  onClick={navigateToProPlan}
                >
                  {proPlan.buttonText || 'Start Today'}
                </button>
                
                <h3 className="font-bold mb-3">Features</h3>
                <ul className="space-y-3">
                  {proPlan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <svg
                        width={20}
                        height={20}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#F46E3E"
                        strokeWidth={2}
                        className="mr-2 mt-0.5 flex-shrink-0"
                      >
                        <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span className="text-sm text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default TrialModal